import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import {
  EWarehouseProductTrendScenario,
  type IReportWarehouseProduct,
  type IReportWarehouseProductTrend,
  type IWarehouseDetail,
} from '@mahawi/eshop-common/src/types';
import {
  Button,
  Col,
  Divider,
  Input,
  Popover,
  Row,
  Select,
  Space,
  Tag,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import { type DefaultOptionType } from 'antd/lib/select';
import dayjs from 'dayjs';
import React, { type ChangeEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import { EDateFormat } from '../fragments/date-format';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { reportWarehouseProductTrendLoad } from '../reducers/report/reducer';
import { SPACING } from '../theme/tokens';

interface CountPrice {
  count: number;
  price: number;
  deltaCount: number;
  deltaPrice: number;
}

interface DataType {
  key: string;
  warehouseProductUuid: string;
  name: string;
  [key: string]: CountPrice | string;
}

const getColorByCount = (count: number | undefined): string => {
  if (!count) {
    return 'warning';
  }

  if (count < 0) {
    return 'success';
  }

  return 'error';
};

function ReportWarehouseProductTrendContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const warehouseWarehouses = useSelector(
    (s: RootState) => s.Warehouse.warehouses,
  );
  const warehouseUpdatedAt = useSelector(
    (s: RootState) => s.Warehouse.updatedAt,
  );
  const reportWarehouseProductTrend = useSelector(
    (s: RootState) => s.Report.warehouseProductTrend,
  );
  const reportUpdatedAt = useSelector((s: RootState) => s.Report.updatedAt);
  const reportInProcess = useSelector((s: RootState) => s.Report.inProcess);

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [columns, setColumns] = useState<ColumnsType<object>>([]);
  const [dayInterval, setDayInterval] = useState<number>(3);
  const [warehouseProductsCount, setwarehouseProductsCount]: [
    number,
    React.Dispatch<React.SetStateAction<number>>,
  ] = useState<number>(100);
  const [excludeAssignedProducts, setExcludeAssignedProducts]: [
    boolean,
    React.Dispatch<React.SetStateAction<boolean>>,
  ] = useState<boolean>(true);

  const [warehouseOptions, setWarehouseOptions] = useState<DefaultOptionType[]>(
    [],
  );
  const [warehouseUuid, setWarehouseUuid] = useState<string | null>(null);
  const [minimalWeight, setMinimalWeight] = useState<number>(0.001);
  const [maximalWeight, setMaximalWeight] = useState<number>(30);
  const [minimalPrice, setMinimalPrice] = useState<number>(0);
  const [maximalPrice, setMaximalPrice] = useState<number>(60000);
  const [scenario, setScenario] = useState<EWarehouseProductTrendScenario>(
    EWarehouseProductTrendScenario.SELL_COUNT,
  );

  useEffect((): void => {
    const warehouseOptionsUE: DefaultOptionType[] =
      warehouseWarehouses
        ?.filter((warehouse: IWarehouseDetail): boolean => warehouse.isActive)
        .map((warehouse: IWarehouseDetail): DefaultOptionType => ({
          label: warehouse.name,
          value: warehouse.uuid,
        })) || [];

    setWarehouseOptions(warehouseOptionsUE);
  }, [warehouseWarehouses, warehouseUpdatedAt]);

  useEffect((): void => {
    if (!reportWarehouseProductTrend) {
      return;
    }

    const { dateFrom, dateTo, report } = reportWarehouseProductTrend;
    const dateColumnsCount: number = dayjs(dateTo).diff(dayjs(dateFrom), 'day');

    const columnsUE: ColumnsType<object> = [
      {
        title: 'Warehuse product UUID',
        dataIndex: 'warehouseProductUuid',
        render: (_text, record: DataType): React.ReactElement => (
          <Link
            to={`/downloader/warehouse-product/${record.warehouseProductUuid}`}
            openInNewTab
            label={record.name}
          />
        ),
      },
    ];

    Array.from({ length: dateColumnsCount }, (_, i: number): number =>
      columnsUE.push({
        title: dayjs(dateFrom).add(i, 'day').format(EDateFormat.DATE),
        dataIndex: dayjs(dateFrom).add(i, 'day').format(EDateFormat.DATE),
        render: (text: CountPrice | undefined): React.ReactElement => {
          const { count, price, deltaCount, deltaPrice } = text || {};

          return (
            <>
              <Row>
                <Col span={12}>
                  <Tag color={getColorByCount(deltaCount)}>
                    <Popover
                      trigger="hover"
                      placement="top"
                      content="Delta stock count by previous day."
                    >
                      {deltaCount || 0}
                    </Popover>
                  </Tag>
                </Col>
                <Col span={12}>
                  <Tag color={getColorByCount(deltaPrice)}>
                    <Popover
                      trigger="hover"
                      placement="top"
                      content="Delta price by previous day."
                    >
                      {deltaPrice || 0}
                    </Popover>
                  </Tag>
                </Col>
              </Row>
              <Row>
                <Col span={12}>
                  <Popover
                    trigger="hover"
                    placement="bottom"
                    content="Actual stock count for day."
                  >
                    {count || 0}
                  </Popover>
                </Col>
                <Col span={12}>
                  <Popover
                    trigger="hover"
                    placement="bottom"
                    content="Actual price for day."
                  >
                    {price || 0}
                  </Popover>
                </Col>
              </Row>
            </>
          );
        },
      }),
    );

    setColumns(columnsUE);

    const dataSourceUE: DataType[] = Object.keys(report)
      .map((uuid: string): DataType | null => {
        if (!reportWarehouseProductTrend) {
          return null;
        }

        const item: IReportWarehouseProduct = report[uuid];

        const row: DataType = {
          key: uuid,
          warehouseProductUuid: uuid,
          name: item.name,
        };

        item.trends.forEach(
          (reportItem: IReportWarehouseProductTrend): void => {
            row[reportItem.date] = {
              count: reportItem.count,
              price: reportItem.price,
              deltaCount: reportItem.delta.count,
              deltaPrice: reportItem.delta.price,
            };
          },
        );

        return row;
      })
      .filter(notEmpty);

    setDataSource(dataSourceUE);
  }, [
    reportWarehouseProductTrend,
    dayInterval,
    warehouseProductsCount,
    reportUpdatedAt,
  ]);

  return (
    <>
      <DetailPageHeader
        title="Report warehouse product trend"
        onReload={(): void => {
          if (!warehouseUuid) {
            return;
          }

          dispatch(
            reportWarehouseProductTrendLoad(
              warehouseUuid,
              dayInterval,
              warehouseProductsCount,
              excludeAssignedProducts,
              minimalWeight,
              maximalWeight,
              minimalPrice,
              maximalPrice,
              scenario,
            ),
          );
        }}
        isReloadDisabled={!warehouseUuid || reportInProcess}
        isReloadHighlighted={!reportWarehouseProductTrend}
      />

      <Space size={SPACING.lg}>
        <Select
          style={{ width: 200 }}
          placeholder="Select warehouse"
          showSearch={{ optionFilterProp: 'label' }}
          options={warehouseOptions}
          onChange={(value: string): void => {
            setWarehouseUuid(value);
          }}
        />

        <Space size={SPACING.lg} orientation="vertical">
          <Select
            style={{ width: 200 }}
            placeholder="Select days ago"
            showSearch={{ optionFilterProp: 'label' }}
            options={[
              { label: '3 days ago', value: '3' },
              { label: '7 days ago', value: '7' },
              { label: '14 days ago', value: '14' },
              { label: '30 days ago', value: '30' },
            ]}
            value={String(dayInterval)}
            onChange={(value: string): void => {
              setDayInterval(Number(value));
            }}
          />

          <Select
            style={{ width: 200 }}
            placeholder="Select scenario"
            showSearch={{ optionFilterProp: 'label' }}
            options={Object.values(EWarehouseProductTrendScenario).map(
              (
                scenarioOption: EWarehouseProductTrendScenario,
              ): DefaultOptionType => ({
                label: scenarioOption,
                value: scenarioOption,
              }),
            )}
            value={scenario}
            onChange={(value: EWarehouseProductTrendScenario): void => {
              setScenario(value);
            }}
          />
        </Space>

        <Space size={SPACING.lg} orientation="vertical">
          <Select
            style={{ width: 200 }}
            placeholder="Select count of warehouse products"
            showSearch={{ optionFilterProp: 'label' }}
            options={[
              { label: '100 warehouse products', value: '100' },
              { label: '200 warehouse products', value: '200' },
              { label: '500 warehouse products', value: '500' },
              { label: '1000 warehouse products', value: '1000' },
              { label: '2000 warehouse products', value: '2000' },
            ]}
            value={String(warehouseProductsCount)}
            onChange={(value: string): void => {
              setwarehouseProductsCount(Number(value));
            }}
          />

          <Select
            style={{ width: 200 }}
            placeholder="Select exclude offered products"
            showSearch={{ optionFilterProp: 'label' }}
            options={[
              { label: 'Exclude offered products', value: 'true' },
              { label: 'Include offered products', value: 'false' },
            ]}
            value={String(excludeAssignedProducts)}
            onChange={(value: string): void => {
              setExcludeAssignedProducts(value === 'true');
            }}
          />
        </Space>

        <Space size={SPACING.lg} orientation="vertical">
          <Space.Compact>
            <Button type="default" style={{ pointerEvents: 'none' }}>
              Minimal weight
            </Button>
            <Input
              type="number"
              step="0.001"
              value={minimalWeight}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                setMinimalWeight(Number(event.target.value));
              }}
            />
            <Button type="default" style={{ pointerEvents: 'none' }}>
              kg
            </Button>
          </Space.Compact>

          <Space.Compact>
            <Button type="default" style={{ pointerEvents: 'none' }}>
              Maximal weight
            </Button>
            <Input
              type="number"
              step="0.001"
              value={maximalWeight}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                setMaximalWeight(Number(event.target.value));
              }}
            />
            <Button type="default" style={{ pointerEvents: 'none' }}>
              kg
            </Button>
          </Space.Compact>
        </Space>

        <Space size={SPACING.lg} orientation="vertical">
          <Space.Compact>
            <Button type="default" style={{ pointerEvents: 'none' }}>
              Minimal price
            </Button>
            <Input
              type="number"
              step="0.001"
              value={minimalPrice}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                setMinimalPrice(Number(event.target.value));
              }}
            />
          </Space.Compact>

          <Space.Compact>
            <Button type="default" style={{ pointerEvents: 'none' }}>
              Maximal price
            </Button>
            <Input
              type="number"
              step="0.001"
              value={maximalPrice}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                setMaximalPrice(Number(event.target.value));
              }}
            />
          </Space.Compact>
        </Space>
      </Space>

      <Divider />

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={reportInProcess}
        emptyText="No trend data found"
      />
    </>
  );
}

export default ReportWarehouseProductTrendContainer;
